import React, { useCallback, useEffect, useState } from "react";
import { Button } from "antd";
import { Link } from "react-router-dom";
import { Activity, AlertTriangle, ArrowRight, Clock, RefreshCw, Server, ShieldCheck, Waypoints } from "lucide-react";
import { AN_COLORS, AN_MONO } from "../../../design-system/tokens";
import { PageHeader, PageShell } from "../../../design-system/tableUi";
import { card, Note } from "../ssh-proxy/sshProxyUi";
import ActivityChart, { OUTCOME_COLORS } from "../ssh-access-log/ActivityChart";
import { dashboardSummary } from "../../../service/sshProxyService";

// The dashboard at "/" in PAM mode: POST /dashboard/summary (admins only).
// Each card links to the screen its numbers come from; the chart is the SSH
// access log's, over 24 hourly buckets.

const HOUR = 3600e3;

const errorText = (err) =>
  err?.response?.status === 403
    ? "Administrator role required."
    : err?.response?.data?.error || "Couldn't load the dashboard.";

const fmtUpdated = (t) =>
  t
    ? new Date(t).toLocaleString("en-US", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })
    : "—";

const num = (n) => (n == null ? "—" : Number(n).toLocaleString());

function SummaryCard({ to, icon: Icon, label, value, total, accent = AN_COLORS.primary, lines = [], warn }) {
  const [hover, setHover] = useState(false);
  return (
    <Link
      to={to}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...card,
        padding: "16px 18px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        color: AN_COLORS.ink,
        textDecoration: "none",
        borderColor: hover ? AN_COLORS.primary : AN_COLORS.line,
        boxShadow: hover ? "0 2px 8px rgba(16,22,60,.06)" : "0 1px 2px rgba(16,22,60,.03)",
        transition: "border-color .15s, box-shadow .15s",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              width: 26,
              height: 26,
              borderRadius: 7,
              background: AN_COLORS.primaryL,
              color: accent,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon size={14} />
          </span>
          <span style={{ fontFamily: AN_MONO, fontSize: 10.5, letterSpacing: 1, textTransform: "uppercase", color: AN_COLORS.muted }}>
            {label}
          </span>
        </span>
        <ArrowRight size={14} color={hover ? AN_COLORS.primary : AN_COLORS.muted} />
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span style={{ fontSize: 26, fontWeight: 600, letterSpacing: -0.5, color: accent === AN_COLORS.primary ? AN_COLORS.ink : accent }}>
          {num(value)}
        </span>
        {total != null && <span style={{ fontSize: 14, color: AN_COLORS.muted }}>/ {num(total)}</span>}
      </div>
      {lines.length > 0 && (
        <div style={{ display: "grid", gap: 4 }}>
          {lines.map(([k, v, color]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, color: AN_COLORS.ink3 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                {color && <span style={{ width: 8, height: 8, borderRadius: 2, background: color }} />}
                {k}
              </span>
              <span style={{ fontWeight: 600, color: AN_COLORS.ink2 }}>{num(v)}</span>
            </div>
          ))}
        </div>
      )}
      {warn && (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: AN_COLORS.warn }}>
          <AlertTriangle size={13} />
          {warn}
        </div>
      )}
    </Link>
  );
}

export default function PamDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    dashboardSummary({
      onSuccess: (d) => {
        setData(d);
        setLoading(false);
      },
      onFailure: (err) => {
        setError(errorText(err));
        setLoading(false);
      },
    });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const g = data?.gateways;
  const e = data?.endpoints;
  const p = data?.policies;
  const l = data?.live;
  const d = data?.last24h;
  // Temporary users are hidden for now, so they are neither listed nor counted.
  const liveNow = l ? (l.sessions || 0) + (l.elevations || 0) : null;

  return (
    <PageShell>
      <PageHeader
        eyebrow="PAM · Overview"
        title="Dashboard"
        subtitle={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Clock size={12} />
            Updated {data ? fmtUpdated(data.generatedAt) : "—"}
          </span>
        }
        actions={
          <Button icon={<RefreshCw size={14} />} onClick={load} loading={loading}>
            Refresh
          </Button>
        }
      />

      {error && (
        <div style={{ marginBottom: 14 }}>
          <Note tone="danger">{error}</Note>
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 14,
          marginBottom: 14,
        }}
      >
        <SummaryCard to="/pam/jumpServer" icon={Waypoints} label="Gateways live" value={g?.live} total={g?.total} />
        <SummaryCard
          to="/pam/endpoint"
          icon={Server}
          label="Endpoints online"
          value={e?.online}
          total={e?.total}
          warn={e?.needsSetup > 0 ? `${num(e.needsSetup)} ${e.needsSetup === 1 ? "endpoint needs" : "endpoints need"} setup` : null}
        />
        <SummaryCard
          to="/pam/policy/v2"
          icon={ShieldCheck}
          label="SSH policies"
          value={p?.ssh}
          lines={[
            ["Operators", p?.operators],
          ]}
        />
        <SummaryCard
          to="/pam/auth-logs"
          icon={Activity}
          label="Live now"
          value={liveNow}
          lines={[
            ["Sessions", l?.sessions],
            ["Sudo elevations", l?.elevations],
          ]}
        />
        <SummaryCard
          to="/pam/auth-logs"
          icon={Clock}
          label="Last 24h attempts"
          value={d?.attempts}
          lines={[
            ["Approved", d?.approved, OUTCOME_COLORS.approved],
            ["Denied", d?.denied, OUTCOME_COLORS.denied],
            ["Refused", d?.refused, OUTCOME_COLORS.denied],
            ["Recordings", d?.recordings],
          ]}
        />
      </div>

      {d?.partial && (
        <div style={{ marginBottom: 14 }}>
          <Note tone="warn">The last 24 hours hold more attempts than could be counted — the totals and the chart are a lower bound.</Note>
        </div>
      )}

      <ActivityChart timeline={d?.activity || []} bucketMs={HOUR} loading={loading} />
    </PageShell>
  );
}
